import React, { useState } from "react";
import { customCeil, getyTicks, initalColors } from "./lib/utilty";

const MultiLineChart = ({
  dataSets = [],
  years = [],
  width = 250,
  height = 200,
  title = "",
  xLabel = "",
  yLabel = "",
  labels = [],
}) => {
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const [hoveredLegend, setHoveredLegend] = useState(null);
  const [visibleYears, setVisibleYears] = useState(dataSets.map(() => true)); // Use dataSets length

  const padding = 50;
  // Use data from dataSets directly
  const allDataValues = dataSets.flatMap((ds) => ds.data);
  const maxValue = customCeil(Math.max(...allDataValues));
  const yTicks = getyTicks(maxValue);

  const xScale = (index) =>
    (index / (labels.length - 1)) * (width - padding * 2) + padding;
  const yScale = (value) =>
    height - padding - (value / maxValue) * (height - padding * 2);

  const handleLegendClick = (index) => {
    const updatedVisibility = [...visibleYears];
    updatedVisibility[index] = !updatedVisibility[index];
    setVisibleYears(updatedVisibility);
  };

  const handleMouseEnter = (index) => {
    setHoveredIndex(index);
  };

  const handleMouseLeave = () => {
    setHoveredIndex(null);
  };

  return (
    <div className="relative">
      <svg
        width={width}
        height={height}
        className="border border-gray-300 bg-white"
      >
        {/* Title */}
        <text
          x={width / 2}
          y={padding / 2}
          textAnchor="middle"
          className="text-xl font-bold"
        >
          {title}
        </text>
        {/* X Axis Label */}
        <text
          x={width / 2}
          y={height - padding / 4}
          textAnchor="middle"
          className="text-sm"
        >
          {xLabel}
        </text>
        {/* Y Axis Label */}
        <text
          x={-height / 2}
          y={padding / 4}
          textAnchor="middle"
          transform="rotate(-90)"
          className="text-sm"
        >
          {yLabel}
        </text>
        {/* Y Axis Values */}
        {Array.from({ length: yTicks + 1 }, (_, i) => (
          <text
            key={i}
            x={padding - 10}
            y={yScale((maxValue / yTicks) * i)}
            textAnchor="end"
            alignmentBaseline="middle"
            className="text-xs text-gray-700"
          >
            {(maxValue / yTicks) * i}
          </text>
        ))}
        {/* Grid */}
        {Array.from({ length: yTicks }, (_, i) => (
          <line
            key={i}
            x1={padding}
            y1={(i * (height - padding * 2)) / yTicks + padding}
            x2={width - padding}
            y2={(i * (height - padding * 2)) / yTicks + padding}
            stroke="lightgray"
            strokeDasharray="5,5"
          />
        ))}
        {/* Vertical Line */}
        {hoveredIndex !== null && (
          <line
            x1={xScale(hoveredIndex)}
            y1={padding}
            x2={xScale(hoveredIndex)}
            y2={height - padding}
            stroke="gray"
            strokeDasharray="4,4"
          />
        )}
        {/* Lines and Points for each dataset */}
        {dataSets.map((dataSet, yearIndex) => {
          // Rename 'data' to 'dataSet' to avoid conflict
          if (!visibleYears[yearIndex]) return null;
          const points = dataSet.data // Access the 'data' array within the dataSet object
            .map((point, index) => `${xScale(index)},${yScale(point)}`)
            .join(" ");
          return (
            <g
              key={yearIndex}
              style={{
                opacity:
                  hoveredLegend !== null && hoveredLegend !== yearIndex
                    ? 0.5
                    : 1,
              }}
            >
              <polyline
                fill="none"
                stroke={
                  dataSets[yearIndex].color ||
                  initalColors[yearIndex % initalColors.length]
                } // Use color from dataset
                strokeWidth="3"
                points={points}
              />
              {dataSet.data.map(
                (
                  point,
                  index // Map over dataSet.data
                ) => (
                  <g key={index}>
                    <circle
                      cx={xScale(index)}
                      cy={yScale(point)}
                      r="4"
                      fill={
                        dataSets[yearIndex].color ||
                        initalColors[yearIndex % initalColors.length]
                      } // Use color from dataset
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                    />
                    {/* Vertical Line */}
                    <line
                      x1={xScale(index)}
                      y1={padding}
                      x2={xScale(index)}
                      y2={height - padding}
                      stroke="gray"
                      opacity={0}
                      strokeWidth={4}
                      onMouseEnter={() => handleMouseEnter(index)}
                      onMouseLeave={handleMouseLeave}
                    />
                  </g>
                )
              )}
            </g>
          );
        })}
        {/* X Axis */}
        <line
          x1={padding}
          y1={height - padding}
          x2={width - padding}
          y2={height - padding}
          stroke="black"
        />
        {/* Y Axis */}
        <line
          x1={padding}
          y1={padding}
          x2={padding}
          y2={height - padding}
          stroke="black"
        />
        {/* X Axis Labels */}
        {labels.map((label, index) => (
          <text
            key={index}
            x={xScale(index)}
            y={height - padding / 2}
            textAnchor="middle"
            className="text-xs text-gray-700"
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
          >
            {label}
          </text>
        ))}
      </svg>

      {/* Tooltip */}
      {hoveredIndex !== null && (
        <div
          className="absolute p-2 bg-white border border-gray-300 rounded shadow text-xs"
          style={{
            left: `${xScale(hoveredIndex)}px`,
            top: `${yScale(
              Math.max(...dataSets.map((ds) => ds.data[hoveredIndex] || 0)) // Use data from dataset object
            )}px`,
            transform: "translate(-50%, -100%)",
          }}
        >
          {dataSets.map(
            // Iterate over dataSets
            (dataSet, yearIndex) =>
              visibleYears[yearIndex] && (
                <div
                  key={yearIndex}
                  style={{
                    color:
                      dataSet.color ||
                      initalColors[yearIndex % initalColors.length],
                  }} // Use color from dataset
                >
                  {dataSet.name}: {dataSet.data[hoveredIndex]}{" "}
                  {/* Use name and data from dataset */}
                </div>
              )
          )}
        </div>
      )}

      {/* Legend */}
      <div className="flex justify-center mt-2">
        {dataSets.map(
          (
            dataSet,
            index // Iterate over dataSets
          ) => (
            <div
              key={index}
              className="flex items-center mx-4 cursor-pointer"
              onMouseEnter={() => setHoveredLegend(index)}
              onMouseLeave={() => setHoveredLegend(null)}
              onClick={() => handleLegendClick(index)}
              style={{
                opacity:
                  hoveredLegend !== null && hoveredLegend !== index ? 0.5 : 1,
              }}
            >
              <span
                style={{
                  backgroundColor: visibleYears[index]
                    ? dataSet.color || initalColors[index % initalColors.length] // Use color from dataset
                    : "transparent",
                  borderColor:
                    dataSet.color || initalColors[index % initalColors.length], // Use color from dataset
                }}
                className={`inline-block w-4 h-4 mr-2 border ${
                  visibleYears[index] ? "" : "border-solid"
                }`}
              ></span>
              <span className="text-sm text-gray-700">{dataSet.name}</span>{" "}
              {/* Use name from dataset */}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default MultiLineChart;
